import { FlexBox } from 'shared/components/Base/FlexBox'
import { Text } from 'shared/components/Text/Text'
import { formatCurrency } from 'shared/helpers/formatters'
import { cyanD1, darkGray, kellyGreenD1, lightGray1, midGray, royalBlue } from 'shared/theme/colors'

import { useUserProvider } from '../../components/Context/UserContext'
import { ctEstimateFormatter, ctRangeFormatter } from '../../helpers/formatters'
import { getTripRate, getTripRatePerMile } from '../../helpers/job'
import { useContainerBreakpoint } from '../../hooks/useBreakpoint'
import { JobStatusDetail } from '../../models/job'
import { Load } from '../../models/load'
import { breakpointsSize } from '../../theme/breakpoints'

interface LoadRateProps {
  load: Load
  isInline?: boolean
  wrapTripRate?: boolean
  showEstimate?: boolean
  showEstimateRow?: boolean
  confirmedRate?: number
  className?: string
  jobStatus?: JobStatusDetail
  isSmall?: boolean
  children?: React.ReactNode
  hasStarterFeature?: React.ReactNode
  isJob?: boolean
  isListedPrice?: boolean
}

export function LoadRate({
  load,
  isInline,
  showEstimate = true,
  showEstimateRow = false,
  wrapTripRate = false,
  confirmedRate,
  className,
  jobStatus,
  isSmall,
  children,
  isJob,
  isListedPrice,
}: LoadRateProps) {
  const {
    permissions: { canViewRates },
    subscription,
  } = useUserProvider()
  const { hasStarterFeature } = subscription || {}
  const tripRate = getTripRate(load.tripRate || 0, confirmedRate)
  const tripRatePerMile = getTripRatePerMile(tripRate, load.tripDistanceMi || 0)
  const hasEstimates = load.estimatedRateMin && load.estimatedRateMax
  const ctRange = load && hasEstimates && ctRangeFormatter(load)
  const ctEstimate = load && hasEstimates && ctEstimateFormatter(load)
  const isInstantBook = load.instantBook
  const [containerRef, isMobile] = useContainerBreakpoint(breakpointsSize.md)

  function renderRate() {
    if (tripRate) {
      return (
        <FlexBox
          gap={isSmall ? '0px' : '10px'}
          alignItems={isSmall ? 'flex-start' : 'flex-end'}
          isColumn={wrapTripRate || isSmall}
        >
          <Text variant="header2" fontColor={darkGray} className="listed-price">
            {isListedPrice ? 'Listed price: ' : ''}
            {formatCurrency(tripRate)}
          </Text>
          <Text
            variant={isSmall ? 'label3' : 'header3'}
            fontColor={lightGray1}
            mb={isInline ? -1.5 : 0}
            className="rate-per-mile"
          >
            {formatCurrency(tripRatePerMile)} /mi
          </Text>
        </FlexBox>
      )
    }

    // if has the new load board update and is mobile, don't show the rate
    if (!(isMobile && isSmall))
      return (
        <Text
          variant={isSmall ? 'header4' : 'label1'}
          fontColor={lightGray1}
          fontWeight={600}
          className="price-to-be-confirmed"
        >
          Rate to be confirmed
        </Text>
      )

    return null
  }

  function renderEstimate() {
    if (!showEstimate) return null

    // in the new design we don't show estimate for loads with rates
    if (isSmall && tripRate) return null

    return (
      <FlexBox ref={containerRef} isColumn={!showEstimateRow} gap={showEstimateRow ? '12px' : undefined}>
        {ctEstimate &&
          (isSmall && isMobile ? (
            <FlexBox isColumn>
              <Text variant="header3" fontColor={cyanD1} className="ct-estimate">
                CT Estimate
              </Text>
              <Text variant="header3" fontColor={cyanD1} className="ct-estimate">
                {ctEstimate}
              </Text>
            </FlexBox>
          ) : (
            <Text variant="header3" fontColor={cyanD1} className="ct-estimate">
              CT Estimate{isListedPrice ? ': ' : ' '}
              {ctEstimate}
            </Text>
          ))}
        {ctRange && !(isMobile && isSmall) && (
          <Text
            variant={isSmall ? 'label3' : 'paragraph1'}
            fontColor={isSmall ? midGray : lightGray1}
            className="ct-range"
          >
            {ctRange}
          </Text>
        )}
      </FlexBox>
    )
  }

  if (!canViewRates || jobStatus === JobStatusDetail.CANCELED) return null

  return (
    <FlexBox
      ref={containerRef}
      justifyContent="flex-start"
      alignItems="flex-start"
      isColumn={!isInline}
      className={className}
      gap={isSmall ? '0px' : '24px'}
    >
      {renderRate()}
      {renderEstimate()}
      {children}
      {isSmall && !hasStarterFeature && isInstantBook && !isJob && (
        <FlexBox>
          <Text variant="label3" fontColor={isInstantBook ? kellyGreenD1 : royalBlue}>
            {isInstantBook ? 'Instant book' : 'Standard book'}
          </Text>
        </FlexBox>
      )}
    </FlexBox>
  )
}
